/**
 * Keeps `^anchor` ids glued to the end of their task line while editing.
 *
 * The server appends ` ^t1a2b3` to every task line; the id links the line to its item. Edits must
 * never move an id onto another line or into the middle of text:
 * - Enter on an anchored task line keeps the id on the current line (the new line is a new task).
 * - Backspace at a line start / Delete at the end of the text joins lines in front of the id.
 * - After any other local edit, ids that ended up in the middle of a line move back to its end, ids
 *   on lines that are no longer task lines are dropped, and a line keeps one id: its own one if
 *   the edit started inside its text.
 * - Rich mode: the cursor never rests behind the hidden id, so typing at the end of a line
 *   (including spaces) goes before it.
 */
import { EditorSelection, EditorState, Prec, type ChangeSpec, type Extension, type Transaction } from "@codemirror/state";
import { keymap, type EditorView } from "@codemirror/view";
import { insertNewlineContinueMarkup } from "@codemirror/lang-markdown";

const TASK_PREFIX = /^(\s*)([-*+]|\d+[.)])\s+\[[ xX]\](?:\s|$)/;
const LIST_PREFIX = /^\s*(?:(?:[-*+]|\d+[.)])\s+(?:\[[ xX]\]\s?)?)?/;
/** An id token: preceded by whitespace (or line start), not followed by an id character. */
const ANCHOR = /(^|[ \t])\^([a-z0-9]{4,12})(?![a-z0-9])/g;
/** The id at the very end of a line, with the one separator space in front of it. */
const TRAILING_ANCHOR = /[ \t]?\^([a-z0-9]{4,12})[ \t]*$/;

type Cmd = (target: { state: EditorState; dispatch: (tr: Transaction) => void }) => boolean;

export interface AnchorMatch {
  /** Start of the token including its separator whitespace (offset in the line). */
  from: number;
  to: number;
  id: string;
}

export function findAnchors(text: string): AnchorMatch[] {
  return [...text.matchAll(ANCHOR)].map((m) => ({ from: m.index, to: m.index + m[0].length, id: m[2] }));
}

/** Offset in `text` where the trailing id of a task line (with its separator) starts, or null. */
export function trailingAnchorStart(text: string): number | null {
  if (!TASK_PREFIX.test(text)) return null;
  const m = TRAILING_ANCHOR.exec(text);
  return m ? m.index : null;
}

function stripAnchors(text: string): string {
  let out = text;
  for (const a of findAnchors(text).reverse()) {
    // Keep the separator only when the id was glued between two words.
    const keepSep = a.from > 0 && /\S/.test(out.slice(a.to, a.to + 1));
    out = out.slice(0, a.from) + (keepSep ? out[a.from] : "") + out.slice(a.to);
  }
  return out;
}

/**
 * The normalized form of one line, or null when nothing has to change: a task line keeps one id
 * (`prefer` if given, else its first) at its end, other lines lose their ids.
 */
export function normalizeAnchorLine(text: string, prefer?: string): string | null {
  const anchors = findAnchors(text);
  if (!anchors.length) return null;
  if (!TASK_PREFIX.test(text)) return stripAnchors(text);
  const id = prefer ?? anchors[0].id;
  if (anchors.length === 1 && anchors[0].id === id && /^[ \t]*$/.test(text.slice(anchors[0].to))) return null;
  return `${stripAnchors(text).replace(/[ \t]+$/, "")} ^${id}`;
}

/** Minimal change turning `from` into `to` at `offset`, so cursors in untouched text stay put. */
function diffChange(offset: number, from: string, to: string): ChangeSpec {
  let start = 0;
  while (start < from.length && start < to.length && from[start] === to[start]) start++;
  let endA = from.length;
  let endB = to.length;
  while (endA > start && endB > start && from[endA - 1] === to[endB - 1]) {
    endA--;
    endB--;
  }
  return { from: offset + start, to: offset + endA, insert: to.slice(start, endB) };
}

/** Fix ids moved by local typing, deleting or pasting (remote and undo edits are left alone). */
const normalizeOnEdit = EditorState.transactionFilter.of((tr) => {
  if (!tr.docChanged || !(tr.isUserEvent("input") || tr.isUserEvent("delete") || tr.isUserEvent("move"))) return tr;
  const prefer = new Map<number, string>();
  const touched = new Set<number>();
  tr.changes.iterChanges((fromA, toA, fromB, toB) => {
    const first = tr.newDoc.lineAt(fromB).number;
    const last = tr.newDoc.lineAt(toB).number;
    for (let n = first; n <= last; n++) touched.add(n);
    // A deletion that starts inside a task line's text and swallows its id keeps that line's id.
    const startLine = tr.startState.doc.lineAt(fromA);
    const start = trailingAnchorStart(startLine.text);
    if (start === null || toA <= startLine.to) return;
    const id = TRAILING_ANCHOR.exec(startLine.text)![1];
    if (fromA > startLine.from + TASK_PREFIX.exec(startLine.text)![0].length) prefer.set(first, id);
  });
  const changes: ChangeSpec[] = [];
  for (const n of touched) {
    const line = tr.newDoc.line(n);
    const next = normalizeAnchorLine(line.text, prefer.get(n));
    if (next !== null) changes.push(diffChange(line.from, line.text, next));
  }
  return changes.length ? [tr, { changes, sequential: true }] : tr;
});

/**
 * Enter on an anchored task line: the id stays on this line. At the end of the text a fresh task
 * line follows; in the middle, the text after the cursor becomes a new task below.
 */
export const enterKeepsAnchor: Cmd = ({ state, dispatch }) => {
  const sel = state.selection.main;
  if (state.selection.ranges.length > 1) return false;
  const line = state.doc.lineAt(sel.from);
  if (state.doc.lineAt(sel.to).number !== line.number) return false;
  const start = trailingAnchorStart(line.text);
  if (start === null) return false;
  const prefix = TASK_PREFIX.exec(line.text)!;
  const anchorFrom = line.from + start;
  if (sel.from <= line.from + prefix[0].length || sel.to > anchorFrom) return false;
  const moved = state.sliceDoc(sel.to, anchorFrom).replace(/^[ \t]+/, "");
  const head = `\n${prefix[1]}${/\d/.test(prefix[2]) ? `${parseInt(prefix[2], 10) + 1}${prefix[2].slice(-1)}` : prefix[2]} [ ] `;
  dispatch(
    state.update({
      changes: [
        { from: sel.from, to: anchorFrom, insert: "" },
        { from: line.to, insert: head + moved },
      ],
      selection: EditorSelection.cursor(line.to - (anchorFrom - sel.from) + head.length),
      scrollIntoView: true,
      userEvent: "input",
    }),
  );
  return true;
};

/** Join line `n` onto line `n - 1` when the latter ends with an id: the text goes in front of it. */
function joinOntoAnchored(state: EditorState, n: number): Transaction | null {
  if (n < 2) return null;
  const prev = state.doc.line(n - 1);
  const start = trailingAnchorStart(prev.text);
  if (start === null) return null;
  const cur = state.doc.line(n);
  const rest = stripAnchors(cur.text.replace(LIST_PREFIX, "")).replace(/[ \t]+$/, "");
  const head = prev.text.slice(0, start);
  const sep = rest && head && !/\s$/.test(head) ? " " : "";
  return state.update({
    changes: { from: prev.from + start, to: cur.to, insert: sep + rest + prev.text.slice(start) },
    selection: EditorSelection.cursor(prev.from + start),
    scrollIntoView: true,
    userEvent: "delete",
  });
}

/** Backspace at the very start of a line below an anchored task line. */
export const backspaceKeepsAnchor: Cmd = ({ state, dispatch }) => {
  const sel = state.selection.main;
  if (state.selection.ranges.length > 1 || !sel.empty) return false;
  const line = state.doc.lineAt(sel.head);
  if (sel.head !== line.from) return false;
  const tr = joinOntoAnchored(state, line.number);
  if (tr) dispatch(tr);
  return !!tr;
};

/** Delete at the end of an anchored task line's text (in front of the id) joins the next line. */
export const deleteKeepsAnchor: Cmd = ({ state, dispatch }) => {
  const sel = state.selection.main;
  if (state.selection.ranges.length > 1 || !sel.empty) return false;
  const line = state.doc.lineAt(sel.head);
  const start = trailingAnchorStart(line.text);
  if (start === null || sel.head < line.from + start || line.number >= state.doc.lines) return false;
  const tr = joinOntoAnchored(state, line.number + 1);
  if (tr) dispatch(tr);
  return !!tr;
};

/** Enter / Backspace / Delete that never drag an id along, plus the normalizer. Both modes. */
export function anchorGuard(): Extension {
  return [
    Prec.highest(
      keymap.of([
        { key: "Enter", run: (view: EditorView) => enterKeepsAnchor(view) || insertNewlineContinueMarkup(view) },
        { key: "Backspace", run: backspaceKeepsAnchor },
        { key: "Delete", run: deleteKeepsAnchor },
      ]),
    ),
    normalizeOnEdit,
  ];
}

/** Rich mode: a cursor placed behind the hidden id of a task line is moved in front of it. */
export const richAnchorCursor = EditorState.transactionFilter.of((tr) => {
  if (!tr.selection || tr.selection.ranges.length > 1) return tr;
  const sel = tr.selection.main;
  if (!sel.empty) return tr;
  const line = tr.newDoc.lineAt(sel.head);
  if (sel.head !== line.to) return tr;
  const start = trailingAnchorStart(line.text);
  if (start === null) return tr;
  return [tr, { selection: EditorSelection.cursor(line.from + start), sequential: true }];
});
